import type { RegistryRecord } from '../../../api/registryRecordApi';
import { PHASE_STATUS_META, type PhaseStatus } from '../../../components/registry/RegistryPhaseWorkspace';

export { PHASE_STATUS_META };
export type { PhaseStatus };

/**
 * Fazele fluxului dermato pentru pacienții importați:
 * 1. Conștientizare & Anamneză · 2. Screening & Diagnostic AI ·
 * 3. Tratament & Chirurgie Plastică · 4. Registru Dermato & Follow-up.
 */

export type DermatoPhase = 1 | 2 | 3 | 4;

export const DERMATO_PHASES = [
  { phase: 1 as const, title: 'Conștientizare & Anamneză' },
  { phase: 2 as const, title: 'Screening & Diagnostic AI' },
  { phase: 3 as const, title: 'Tratament & Chirurgie Plastică' },
  { phase: 4 as const, title: 'Registru Dermato & Follow-up' },
];

const isHospitalEncounter = (e: { type: string }) => e.type === 'HOSPITALIZATION' || e.type === 'OBSERVATION';

const hasQuestionnaireContent = (record: RegistryRecord): boolean => {
  const q = record.questionnaire;
  if (!q) return false;
  return Object.values(q).some(
    (s) => s && typeof s === 'object' && !Array.isArray(s) && Object.keys(s as object).length > 0,
  );
};

/** Documentele de imagistică + FotoFinder (kind: `imagistica` → IMAGING_REPORT, `fotofinder` → FOTOFINDER_REPORT). */
export const imagingDocuments = (record: RegistryRecord) =>
  record.documents.filter((d) => d.kind === 'IMAGING_REPORT' || d.kind === 'FOTOFINDER_REPORT');

/** Toate intervențiile chirurgicale, cu codul leziunii atașat. */
export const allSurgeries = (record: RegistryRecord) =>
  record.cases.flatMap((c) =>
    (c.surgeries ?? []).map((s) => ({ ...s, caseCode: c.code })),
  );

/** Statusul celor 4 faze dermato, derivat din fișa de registru importată. */
export function computePhaseStatuses(record: RegistryRecord): Record<DermatoPhase, PhaseStatus> {
  // Faza 1 — chestionarul
  const faza1: PhaseStatus = hasQuestionnaireContent(record) ? 'COMPLETE' : 'MISSING';

  // Faza 2 — HP + (imagistică/FotoFinder sau leziune) = completă; orice bucată = parțială
  const lesions = record.cases.length;
  const withHp = record.cases.filter((c) => c.histopathology).length;
  const imaging = imagingDocuments(record).length > 0;
  const faza2: PhaseStatus =
    withHp >= 1 && (imaging || lesions >= 1)
      ? 'COMPLETE'
      : lesions > 0 || imaging
        ? 'PARTIAL'
        : 'MISSING';

  // Faza 3 — intervenție sau internare cu proceduri = completă; internare fără proceduri = parțială
  const surgeries = allSurgeries(record).length > 0;
  const hospitalizations = record.encounters.filter(isHospitalEncounter);
  const withProcedures = hospitalizations.some((e) => (e.procedures?.length ?? 0) > 0);
  const faza3: PhaseStatus =
    surgeries || withProcedures ? 'COMPLETE' : hospitalizations.length > 0 ? 'PARTIAL' : 'MISSING';

  // Faza 4 — caz cu diagnostic = completă; fișă fără diagnostic = parțială
  const faza4: PhaseStatus = record.cases.some((c) => c.diagnosis) ? 'COMPLETE' : 'PARTIAL';

  return { 1: faza1, 2: faza2, 3: faza3, 4: faza4 };
}
